import { useState, useCallback, useMemo } from "react";
import { MenuWithItems, MenuItem as MenuItemType } from "@/hooks/useAllDailyMenus";
import { useCart } from "@/contexts/CartContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Plus, Minus, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { isOnlineOrderingOpen } from "@/lib/orderingWindow";

// ── Types ───────────────────────────────────────────────────────────────────

interface RestaurantCardMenuProps {
  menusWithItems?: MenuWithItems[];
  isLoading?: boolean;
  restaurantId?: string;
  restaurantName?: string;
  hasOnlineOrdering?: boolean;
  menuStartTime?: string | null;
  menuEndTime?: string | null;
  onlineOrderingEndTime?: string | null;
  menuDate?: Date;
  orderingLogicType?: string | null;
  soupOnlyPrice?: number | null;
  mainCourseOnlyPrice?: number | null;
  dineInAvailable?: boolean;
  deliveryAvailable?: boolean;
  takeawayAvailable?: boolean;
  deliveryFee?: number | null;
  boxPrice?: number | null;
  minOrderAmount?: number | null;
  passCommissionToCustomer?: boolean;
  commissionPercent?: number | null;
  commissionPerOrder?: number | null;
  commissionVatApplicable?: boolean;
  commissionVatRate?: number | null;
  restaurantDeliveryPrice?: number | null;
  restaurantTakeawayPrice?: number | null;
}

interface MergedMenuGroup {
  menuTypeId: string | null;
  menuTypeName: string;
  menus: MenuWithItems[];
  allItems: MenuItemType[];
  dineInPrice: number | null;
  deliveryPrice: number | null;
  takeawayPrice: number | null;
  primaryMenuId: string;
}

type OrderingLogic = "category_radio" | "variant_radio" | "complete_package" | "item_price" | "mixed";

const SELECTED_BG = "bg-[#c4e4f4]";

// ── Helpers ─────────────────────────────────────────────────────────────────

function isMenuVariantName(name: string): boolean {
  return /men[üű]/i.test(name);
}

function isSoupCategory(name: string): boolean {
  return /leves/i.test(name);
}

function isMainCourseCategory(name: string): boolean {
  return /f[őö](étel|fogás)|főétel|foetel|második/i.test(name);
}

function stripPriceFromName(name: string, menuTypeName?: string): string {
  if (menuTypeName && menuTypeName.toLowerCase() !== "mai menü") return name;
  return name.replace(/\s*-\s*[\d\s.,]+\s*Ft\.?-?\s*$/i, "").trim();
}

function groupItemsByFoodType(items: MenuItemType[]): Record<string, { items: MenuItemType[]; order: number }> {
  const grouped: Record<string, { items: MenuItemType[]; order: number }> = {};
  for (const item of items) {
    const foodTypeName = item.food_type?.name || "Egyéb";
    if (!grouped[foodTypeName]) {
      grouped[foodTypeName] = { items: [], order: item.food_type?.display_order ?? 999 };
    }
    grouped[foodTypeName].items.push(item);
  }
  return grouped;
}

/** Auto-detect the best ordering logic based on menu structure */
function autoDetectLogic(
  allItems: MenuItemType[],
  hasMenuPrices: boolean,
  foodTypeGroups: Record<string, { items: MenuItemType[]; order: number }>
): OrderingLogic {
  const hasItemPrices = allItems.some(i => i.price != null && i.price > 0);
  const foodTypeNames = Object.keys(foodTypeGroups);

  // If every item has its own price and no menu-level price → item_price
  if (!hasMenuPrices && hasItemPrices) return "item_price";
  if (hasItemPrices && hasMenuPrices) return "mixed";

  // Menu-level pricing modes
  const variantNames = foodTypeNames.filter(isMenuVariantName);
  if (variantNames.length >= 2) return "variant_radio";

  // Check if any category has multiple items → needs radio selection
  const hasMultipleInAnyCategory = foodTypeNames.some(n => foodTypeGroups[n].items.length > 1);
  if (hasMultipleInAnyCategory) return "category_radio";

  // All categories have exactly 1 item → complete package
  return "complete_package";
}

function resolveLogic(
  explicit: string | null | undefined,
  allItems: MenuItemType[],
  hasMenuPrices: boolean,
  foodTypeGroups: Record<string, { items: MenuItemType[]; order: number }>
): OrderingLogic {
  if (explicit && explicit !== "auto") return explicit as OrderingLogic;
  return autoDetectLogic(allItems, hasMenuPrices, foodTypeGroups);
}

// ── Component ───────────────────────────────────────────────────────────────

export function RestaurantCardMenu({
  menusWithItems,
  isLoading = false,
  restaurantId,
  restaurantName,
  hasOnlineOrdering,
  menuStartTime,
  menuEndTime,
  onlineOrderingEndTime,
  menuDate,
  orderingLogicType,
  soupOnlyPrice,
  mainCourseOnlyPrice,
  dineInAvailable = true,
  deliveryAvailable = false,
  takeawayAvailable = false,
  deliveryFee,
  boxPrice,
  minOrderAmount,
  passCommissionToCustomer,
  commissionPercent,
  commissionPerOrder,
  commissionVatApplicable,
  commissionVatRate,
  restaurantDeliveryPrice,
  restaurantTakeawayPrice,
}: RestaurantCardMenuProps) {
  const { addItem } = useCart();

  // Item-price mode selections
  const [selectedItems, setSelectedItems] = useState<
    Map<string, { menuId: string; itemId: string; name: string; price: number; deliveryType: "dine_in" | "delivery" | "takeaway"; quantity: number }>
  >(new Map());

  // Radio selections per food_type category
  const [radioSelections, setRadioSelections] = useState<Map<string, string>>(new Map());

  // Variant mode: selected variant name
  const [variantSelections, setVariantSelections] = useState<Map<string, string>>(new Map());

  // Menu-price delivery type selection
  const [menuDeliverySelections, setMenuDeliverySelections] = useState<
    Map<string, { type: "dine_in" | "delivery" | "takeaway"; price: number; quantity: number }>
  >(new Map());

  const [flyAnimation, setFlyAnimation] = useState(false);

  const orderingOpen = isOnlineOrderingOpen(menuDate, onlineOrderingEndTime);
  const canOrder = Boolean(hasOnlineOrdering) && orderingOpen;

  // ── Merge menu records ──

  const mergedGroups: MergedMenuGroup[] = useMemo(() => {
    if (!menusWithItems || menusWithItems.length === 0) return [];
    const groupMap = new Map<string, MergedMenuGroup>();
    const hasOwnPrice = (m: MenuWithItems["menu"]) =>
      (m.dine_in_price != null && m.dine_in_price > 0) ||
      (m.delivery_price != null && m.delivery_price > 0) ||
      (m.takeaway_price != null && m.takeaway_price > 0);

    for (const mwi of menusWithItems) {
      const baseKey = mwi.menu.menu_type_id || mwi.menu.id;
      const ownPrice = hasOwnPrice(mwi.menu);
      const key = ownPrice ? mwi.menu.id : baseKey;

      const existing = groupMap.get(key);
      if (existing && !ownPrice && mwi.menu.menu_type_id) {
        existing.menus.push(mwi);
        existing.allItems.push(...mwi.items);
      } else {
        groupMap.set(key, {
          menuTypeId: mwi.menu.menu_type_id,
          menuTypeName: mwi.menu.menu_type?.name || "Mai menü",
          menus: [mwi],
          allItems: [...mwi.items],
          dineInPrice: mwi.menu.dine_in_price,
          deliveryPrice: mwi.menu.delivery_price,
          takeawayPrice: mwi.menu.takeaway_price,
          primaryMenuId: mwi.menu.id,
        });
      }
    }

    // Fallback: if menu has no prices, use restaurant-level prices
    const groups = Array.from(groupMap.values());
    for (const group of groups) {
      const hasAnyPrice = (group.deliveryPrice ?? 0) > 0 || (group.takeawayPrice ?? 0) > 0 || (group.dineInPrice ?? 0) > 0;
      if (!hasAnyPrice) {
        if (restaurantDeliveryPrice && restaurantDeliveryPrice > 0) group.deliveryPrice = restaurantDeliveryPrice;
        if (restaurantTakeawayPrice && restaurantTakeawayPrice > 0) group.takeawayPrice = restaurantTakeawayPrice;
      }
    }

    return groups;
  }, [menusWithItems, restaurantDeliveryPrice, restaurantTakeawayPrice]);

  // NOTE: No auto-delivery-selection — user must explicitly choose delivery/takeaway to opt-in

  // ── Callbacks ──

  const defaultItemDeliveryType = takeawayAvailable ? "takeaway" : deliveryAvailable ? "delivery" : "dine_in";

  const toggleItemSelection = useCallback(
    (menuId: string, itemId: string, itemName: string, price: number, deliveryType?: "dine_in" | "delivery" | "takeaway") => {
      const dt = deliveryType || defaultItemDeliveryType;
      const key = `item-${itemId}-${dt}`;
      setSelectedItems(prev => {
        const next = new Map(prev);
        // Remove any existing selection for this item (any delivery type)
        for (const [k] of next) {
          if (k.startsWith(`item-${itemId}-`)) next.delete(k);
        }
        if (prev.has(key)) return next; // was toggling off
        next.set(key, { menuId, itemId, name: itemName, price, deliveryType: dt, quantity: 1 });
        return next;
      });
    },
    [defaultItemDeliveryType]
  );

  const updateItemDeliveryType = useCallback((itemId: string, oldType: string, newType: "dine_in" | "delivery" | "takeaway") => {
    setSelectedItems(prev => {
      const oldKey = `item-${itemId}-${oldType}`;
      const newKey = `item-${itemId}-${newType}`;
      const next = new Map(prev);
      const item = next.get(oldKey);
      if (item) {
        next.delete(oldKey);
        next.set(newKey, { ...item, deliveryType: newType });
      }
      return next;
    });
  }, []);

  const updateSelectionQuantity = useCallback((key: string, delta: number) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const item = next.get(key);
      if (item) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) next.delete(key);
        else next.set(key, { ...item, quantity: newQty });
      }
      return next;
    });
  }, []);

  const toggleMenuDelivery = useCallback(
    (groupKey: string, type: "dine_in" | "delivery" | "takeaway", price: number) => {
      setMenuDeliverySelections(prev => {
        const next = new Map(prev);
        const current = prev.get(groupKey);
        if (current?.type === type) {
          next.delete(groupKey);
        } else {
          next.set(groupKey, { type, price, quantity: current?.quantity || 1 });
        }
        return next;
      });
    },
    []
  );

  const updateMenuQuantity = useCallback((groupKey: string, delta: number) => {
    setMenuDeliverySelections(prev => {
      const next = new Map(prev);
      const sel = next.get(groupKey);
      if (sel) {
        const newQty = sel.quantity + delta;
        if (newQty <= 0) next.delete(groupKey);
        else next.set(groupKey, { ...sel, quantity: newQty });
      }
      return next;
    });
  }, []);

  const handleRadioChange = useCallback((groupKey: string, foodTypeName: string, itemId: string) => {
    setRadioSelections(prev => {
      const key = `${groupKey}-${foodTypeName}`;
      const next = new Map(prev);
      if (prev.get(key) === itemId) {
        next.delete(key);
      } else {
        next.set(key, itemId);
      }
      return next;
    });
  }, []);

  const handleVariantChange = useCallback((groupKey: string, variantName: string) => {
    setVariantSelections(prev => {
      const next = new Map(prev);
      if (prev.get(groupKey) === variantName) {
        next.delete(groupKey);
      } else {
        next.set(groupKey, variantName);
      }
      return next;
    });
  }, []);

  // ── Helpers for mixed-mode ──
  const mixedGroupMap = useMemo(() => {
    const map = new Map<string, MergedMenuGroup>();
    for (const group of mergedGroups) {
      const foodTypeGroups = groupItemsByFoodType(group.allItems);
      const hasMenuPrices = (group.deliveryPrice ?? 0) > 0 || (group.takeawayPrice ?? 0) > 0 || (group.dineInPrice ?? 0) > 0;
      const logic = resolveLogic(orderingLogicType, group.allItems, hasMenuPrices, foodTypeGroups);
      if (logic === "mixed") {
        for (const item of group.allItems) {
          map.set(item.id, group);
        }
      }
    }
    return map;
  }, [mergedGroups, orderingLogicType]);

  /** Check if selected items in a mixed group form a complete menu */
  const checkCompleteMenu = useCallback((group: MergedMenuGroup, selectedInGroup: Array<{ itemId: string }>) => {
    const foodTypeGroups = groupItemsByFoodType(group.allItems);
    const foodTypeNames = Object.keys(foodTypeGroups);
    // A complete menu requires at least one selection from each food type that has priced items
    // Variant food types (containing "menü") are treated as a single group - only one needed
    const pricedFoodTypes = foodTypeNames.filter(ft =>
      foodTypeGroups[ft].items.some(i => i.price != null && i.price > 0)
    );
    if (pricedFoodTypes.length === 0) return false;

    // Separate variant and non-variant priced food types
    const variantPricedTypes = pricedFoodTypes.filter(ft => isMenuVariantName(ft));
    const nonVariantPricedTypes = pricedFoodTypes.filter(ft => !isMenuVariantName(ft));

    // Each non-variant priced food type must have at least one selection
    for (const ft of nonVariantPricedTypes) {
      const ftItems = foodTypeGroups[ft].items;
      const hasSelection = selectedInGroup.some(sel =>
        ftItems.some(i => i.id === sel.itemId)
      );
      if (!hasSelection) return false;
    }

    // If there are variant types, at least one variant must have a selection
    if (variantPricedTypes.length > 0) {
      const hasAnyVariantSelection = variantPricedTypes.some(ft => {
        const ftItems = foodTypeGroups[ft].items;
        return selectedInGroup.some(sel => ftItems.some(i => i.id === sel.itemId));
      });
      if (!hasAnyVariantSelection) return false;
    }

    return true;
  }, []);

  // ── Add to cart ──

  const handleAddToCart = useCallback(() => {
    if (!restaurantId || !restaurantName) return;
    let count = 0;

    // Item-price selections (skip items belonging to mixed groups - handled below)
    for (const [, sel] of selectedItems) {
      if (mixedGroupMap.has(sel.itemId)) continue;
      addItem({
        restaurantId,
        restaurantName,
        dailyMenuId: null,
        menuItemId: sel.itemId,
        itemName: sel.name,
        quantity: sel.quantity,
        unitPrice: sel.price,
        deliveryType: sel.deliveryType,
        restaurantDeliveryFee: deliveryFee ?? undefined,
        restaurantBoxPrice: boxPrice ?? undefined,
        restaurantMinOrderAmount: minOrderAmount ?? undefined,
        passCommissionToCustomer: passCommissionToCustomer ?? undefined,
        commissionPercent: commissionPercent ?? undefined,
        commissionPerOrder: commissionPerOrder ?? undefined,
        commissionVatApplicable: commissionVatApplicable ?? undefined,
        commissionVatRate: commissionVatRate ?? undefined,
        foodTypeCount: 1,
      });
      count += sel.quantity;
    }

    // Menu-price and mixed selections
    for (const [groupKey, deliverySel] of menuDeliverySelections) {
      const group = mergedGroups.find(g => g.primaryMenuId === groupKey);
      if (!group) continue;

      const foodTypeGroups = groupItemsByFoodType(group.allItems);
      const hasMenuPrices = (group.deliveryPrice ?? 0) > 0 || (group.takeawayPrice ?? 0) > 0 || (group.dineInPrice ?? 0) > 0;
      const logic = resolveLogic(orderingLogicType, group.allItems, hasMenuPrices, foodTypeGroups);
      const totalFoodTypes = Object.keys(foodTypeGroups).length;

      // ── MIXED MODE ──
      if (logic === "mixed") {
        const groupItemIdSet = new Set(group.allItems.map(i => i.id));
        const selectedInGroup: Array<{ itemId: string; name: string; price: number; quantity: number }> = [];
        for (const [, sel] of selectedItems) {
          if (groupItemIdSet.has(sel.itemId)) {
            selectedInGroup.push(sel);
          }
        }
        if (selectedInGroup.length === 0) continue;

        const isComplete = checkCompleteMenu(group, selectedInGroup);
        if (isComplete) {
          const choices = selectedInGroup.map(s => s.name);
          const noPriceItems = group.allItems.filter(i => !i.price || i.price === 0);
          for (const npi of noPriceItems) {
            choices.push(npi.name);
          }
          const deliveryLabel = deliverySel.type === "delivery" ? "Kiszállítás" : "Elvitel";
          addItem({
            restaurantId,
            restaurantName,
            dailyMenuId: group.primaryMenuId,
            menuItemId: null,
            itemName: `${group.menuTypeName} (${deliveryLabel})`,
            quantity: deliverySel.quantity,
            unitPrice: deliverySel.price,
            deliveryType: deliverySel.type,
            selectedChoices: choices,
            restaurantDeliveryFee: deliveryFee ?? undefined,
            restaurantBoxPrice: boxPrice ?? undefined,
        restaurantMinOrderAmount: minOrderAmount ?? undefined,
        passCommissionToCustomer: passCommissionToCustomer ?? undefined,
        commissionPercent: commissionPercent ?? undefined,
        commissionPerOrder: commissionPerOrder ?? undefined,
        commissionVatApplicable: commissionVatApplicable ?? undefined,
        commissionVatRate: commissionVatRate ?? undefined,
            foodTypeCount: totalFoodTypes,
          });
          count += deliverySel.quantity;
        } else {
          // Not complete → individual item prices, delivery type only determines method
          for (const sel of selectedInGroup) {
            addItem({
              restaurantId,
              restaurantName,
              dailyMenuId: null,
              menuItemId: sel.itemId,
              itemName: sel.name,
              quantity: sel.quantity,
              unitPrice: sel.price,
              deliveryType: deliverySel.type,
              restaurantDeliveryFee: deliveryFee ?? undefined,
              restaurantBoxPrice: boxPrice ?? undefined,
        restaurantMinOrderAmount: minOrderAmount ?? undefined,
        passCommissionToCustomer: passCommissionToCustomer ?? undefined,
        commissionPercent: commissionPercent ?? undefined,
        commissionPerOrder: commissionPerOrder ?? undefined,
        commissionVatApplicable: commissionVatApplicable ?? undefined,
        commissionVatRate: commissionVatRate ?? undefined,
              foodTypeCount: 1,
            });
            count += sel.quantity;
          }
        }
        continue;
      }

      // ── NON-MIXED MODES (category_radio, variant_radio, complete_package) ──
      const choices: string[] = [];

      if (logic === "variant_radio") {
        for (const [ftName, { items }] of Object.entries(foodTypeGroups)) {
          if (isMenuVariantName(ftName)) continue;
          const radioKey = `${groupKey}-${ftName}`;
          const selectedId = radioSelections.get(radioKey);
          if (selectedId) {
            const item = items.find(i => i.id === selectedId);
            if (item) choices.push(item.name);
          } else if (items.length === 1) {
            choices.push(items[0].name);
          }
        }
        const selectedVariantName = variantSelections.get(groupKey);
        if (selectedVariantName && foodTypeGroups[selectedVariantName]) {
          for (const item of foodTypeGroups[selectedVariantName].items) {
            choices.push(item.name);
          }
        }
      } else if (logic === "category_radio") {
        for (const [ftName, { items }] of Object.entries(foodTypeGroups)) {
          const radioKey = `${groupKey}-${ftName}`;
          const selectedId = radioSelections.get(radioKey);
          if (selectedId) {
            const item = items.find(i => i.id === selectedId);
            if (item) choices.push(item.name);
          } else if (items.length === 1) {
            choices.push(items[0].name);
          }
        }
      } else if (logic === "complete_package") {
        for (const { items } of Object.values(foodTypeGroups)) {
          for (const item of items) choices.push(item.name);
        }
      }

      // GUARD: skip if no food was selected
      if (choices.length === 0) continue;

      // Fallback pricing for partial selection (soup-only / main-course-only)
      let finalPrice = deliverySel.price;
      if (logic === "category_radio" || logic === "variant_radio") {
        const selectedCategoryNames: string[] = [];
        for (const [ftName, { items }] of Object.entries(foodTypeGroups)) {
          if (isMenuVariantName(ftName)) continue;
          const radioKey = `${groupKey}-${ftName}`;
          const selectedId = radioSelections.get(radioKey);
          if (selectedId || items.length === 1) {
            selectedCategoryNames.push(ftName);
          }
        }
        if (logic === "variant_radio" && variantSelections.get(groupKey)) {
          selectedCategoryNames.push(variantSelections.get(groupKey)!);
        }

        const totalCategories = Object.keys(foodTypeGroups).length;
        const isPartialSelection = selectedCategoryNames.length < totalCategories;
        if (isPartialSelection) {
          const onlySoup = selectedCategoryNames.every(n => isSoupCategory(n));
          const onlyMainCourse = selectedCategoryNames.every(n => isMainCourseCategory(n));
          if (onlySoup && soupOnlyPrice && soupOnlyPrice > 0) finalPrice = soupOnlyPrice;
          else if (onlyMainCourse && mainCourseOnlyPrice && mainCourseOnlyPrice > 0) finalPrice = mainCourseOnlyPrice;
        }
      }

      const deliveryLabel =
        deliverySel.type === "dine_in" ? "Helyben fogyasztás" : deliverySel.type === "delivery" ? "Kiszállítás" : "Elvitel";
      addItem({
        restaurantId,
        restaurantName,
        dailyMenuId: group.primaryMenuId,
        menuItemId: null,
        itemName: `${group.menuTypeName} (${deliveryLabel})`,
        quantity: deliverySel.quantity,
        unitPrice: finalPrice,
        deliveryType: deliverySel.type,
        selectedChoices: choices,
        restaurantDeliveryFee: deliveryFee ?? undefined,
        restaurantBoxPrice: boxPrice ?? undefined,
        restaurantMinOrderAmount: minOrderAmount ?? undefined,
        passCommissionToCustomer: passCommissionToCustomer ?? undefined,
        commissionPercent: commissionPercent ?? undefined,
        commissionPerOrder: commissionPerOrder ?? undefined,
        commissionVatApplicable: commissionVatApplicable ?? undefined,
        commissionVatRate: commissionVatRate ?? undefined,
        foodTypeCount: totalFoodTypes,
      });
      count += deliverySel.quantity;
    }

    setSelectedItems(new Map());
    setMenuDeliverySelections(new Map());
    setRadioSelections(new Map());
    setVariantSelections(new Map());
    setFlyAnimation(true);
    setTimeout(() => setFlyAnimation(false), 600);
    if (count > 0) {
      toast.success(`${count} tétel hozzáadva a kosárhoz!`);
    } else {
      toast.error("Válassz ételt és szállítási módot a kosárba rakáshoz!");
    }
  }, [
    selectedItems,
    menuDeliverySelections,
    radioSelections,
    variantSelections,
    restaurantId,
    restaurantName,
    addItem,
    mergedGroups,
    orderingLogicType,
    soupOnlyPrice,
    mainCourseOnlyPrice,
    mixedGroupMap,
    checkCompleteMenu,
    deliveryFee,
    boxPrice,
    minOrderAmount,
    passCommissionToCustomer,
    commissionPercent,
    commissionPerOrder,
    commissionVatApplicable,
    commissionVatRate,
  ]);

  // ── Button validation ──
  const isValidForCart = useMemo(() => {
    // Check individual item selections (non-mixed groups)
    for (const [, sel] of selectedItems) {
      if (!mixedGroupMap.has(sel.itemId)) return true;
    }

    // Check mixed groups: any selected item + delivery selection = valid
    for (const group of mergedGroups) {
      const foodTypeGroups = groupItemsByFoodType(group.allItems);
      const hasMenuPrices = (group.deliveryPrice ?? 0) > 0 || (group.takeawayPrice ?? 0) > 0 || (group.dineInPrice ?? 0) > 0;
      const logic = resolveLogic(orderingLogicType, group.allItems, hasMenuPrices, foodTypeGroups);

      if (logic === "mixed") {
        const groupItemIdSet = new Set(group.allItems.map(i => i.id));
        const hasSelectedItem = Array.from(selectedItems.values()).some(sel => groupItemIdSet.has(sel.itemId));
        if (hasSelectedItem && menuDeliverySelections.has(group.primaryMenuId)) return true;
      }
    }

    // Check menu-price groups: require delivery selection + food selections
    for (const group of mergedGroups) {
      const groupKey = group.primaryMenuId;
      const deliverySel = menuDeliverySelections.get(groupKey);
      if (!deliverySel) continue;

      const foodTypeGroups = groupItemsByFoodType(group.allItems);
      const hasMenuPrices = (group.deliveryPrice ?? 0) > 0 || (group.takeawayPrice ?? 0) > 0 || (group.dineInPrice ?? 0) > 0;
      const logic = resolveLogic(orderingLogicType, group.allItems, hasMenuPrices, foodTypeGroups);

      if (logic === "complete_package") return true;

      if (logic === "category_radio") {
        // Check if at least some food selections exist (auto-selected 1-item categories count)
        let hasAnyFood = false;
        for (const [ftName, { items }] of Object.entries(foodTypeGroups)) {
          if (items.length === 1) { hasAnyFood = true; continue; }
          if (radioSelections.has(`${groupKey}-${ftName}`)) hasAnyFood = true;
        }
        if (hasAnyFood) return true;
      }

      if (logic === "variant_radio") {
        if (variantSelections.has(groupKey)) return true;
      }
    }

    return false;
  }, [selectedItems, menuDeliverySelections, radioSelections, variantSelections, mergedGroups, orderingLogicType, mixedGroupMap]);

  // ── Empty state ──

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-4 flex items-center justify-center gap-2 text-muted-foreground min-h-20">
        <Hourglass className="h-5 w-5 motion-safe:animate-pulse shrink-0" aria-hidden="true" />
        <span className="text-sm">Ajánlatok betöltése…</span>
      </div>
    );
  }

  if (!menusWithItems || menusWithItems.length === 0) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground italic text-center py-4">Nincs feltöltve mai ajánlat, nézz vissza holnap!</p>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="p-4 space-y-6 restaurant-card-content">
      {hasOnlineOrdering && !orderingOpen && (
        <div className="rounded-md border border-muted bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Erre a napra az online rendelés lezárult{onlineOrderingEndTime ? ` (rendelési határidő: ${onlineOrderingEndTime})` : ""}.
        </div>
      )}

      {canOrder && (
        <div className={`transition-transform ${flyAnimation ? "scale-95" : ""}`}>
          <Button
            onClick={handleAddToCart}
            disabled={!isValidForCart}
            className="w-full bg-cart hover:bg-cart/90 text-cart-foreground border border-cart disabled:opacity-40 disabled:cursor-not-allowed"
            variant="outline"
          >
            <ShoppingCart className={`h-4 w-4 mr-2 ${flyAnimation ? "animate-bounce" : ""}`} />
            Kosárba
          </Button>
        </div>
      )}

      {mergedGroups.map(group => {
        const foodTypeGroups = groupItemsByFoodType(group.allItems);
        const sortedFoodTypes = Object.entries(foodTypeGroups).sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999));

        const priceData = [
          group.dineInPrice && group.dineInPrice > 0 && { label: "Helyben fogyasztás", price: group.dineInPrice, type: "dine_in" as const },
          group.takeawayPrice && group.takeawayPrice > 0 && { label: "Elvitel", price: group.takeawayPrice, type: "takeaway" as const },
          group.deliveryPrice && group.deliveryPrice > 0 && { label: "Kiszállítás", price: group.deliveryPrice, type: "delivery" as const },
        ].filter(Boolean) as Array<{ label: string; price: number; type: "dine_in" | "delivery" | "takeaway" }>;

        const hasMenuPrices = priceData.length > 0;
        const logic = resolveLogic(orderingLogicType, group.allItems, hasMenuPrices, foodTypeGroups);
        const groupKey = group.primaryMenuId;
        const deliverySel = menuDeliverySelections.get(groupKey);

        // Determine if food selection is sufficient to enable delivery choice
        const canSelectDelivery = (() => {
          if (logic === "complete_package") return true;
          if (logic === "category_radio") {
            for (const [ftName, { items }] of Object.entries(foodTypeGroups)) {
              if (items.length === 1) continue; // auto-selected
              if (radioSelections.has(`${groupKey}-${ftName}`)) return true;
            }
            // All categories have exactly 1 item → auto-selected
            return Object.values(foodTypeGroups).every(({ items }) => items.length === 1);
          }
          if (logic === "variant_radio") return variantSelections.has(groupKey);
          if (logic === "mixed") {
            const groupItemIdSet = new Set(group.allItems.map(i => i.id));
            return Array.from(selectedItems.values()).some(sel => groupItemIdSet.has(sel.itemId));
          }
          return true;
        })();

        // For mixed mode: check if complete menu is selected
        const isCompleteMenuSelected = logic === "mixed" && (() => {
          const groupItemIdSet = new Set(group.allItems.map(i => i.id));
          const selectedInGroup = Array.from(selectedItems.values()).filter(sel => groupItemIdSet.has(sel.itemId));
          return checkCompleteMenu(group, selectedInGroup);
        })();

        // Variant mode partitioning
        const sharedFoodTypes = sortedFoodTypes.filter(([name]) => !isMenuVariantName(name));
        const variantFoodTypes = sortedFoodTypes.filter(([name]) => isMenuVariantName(name));
        const selectedVariant = variantSelections.get(groupKey) || "";

        // Grid layout
        const colCount = logic === "variant_radio" ? sharedFoodTypes.length + variantFoodTypes.length : sortedFoodTypes.length;
        let gridClass = "grid gap-x-6 gap-y-4 ";
        if (colCount === 1) gridClass += "grid-cols-1";
        else if (colCount === 2) gridClass += "grid-cols-1 md:grid-cols-2";
        else if (colCount === 3) gridClass += "grid-cols-1 md:grid-cols-3";
        else gridClass += "grid-cols-1 md:grid-cols-2";

        return (
          <div key={groupKey} className="space-y-4">
            <h4 className="font-semibold text-lg mb-4 text-primary">{group.menuTypeName}</h4>

            {/* ── VARIANT_RADIO ── */}
            {logic === "variant_radio" ? (
              <div className={gridClass}>
                {/* Shared categories */}
                {sharedFoodTypes.map(([foodTypeName, { items }]) => {
                  const radioKey = `${groupKey}-${foodTypeName}`;
                  const selectedRadio = radioSelections.get(radioKey) || (items.length === 1 ? items[0].id : "");
                  return (
                    <div key={foodTypeName} className="space-y-2 break-inside-avoid">
                      <h5 className="font-medium text-price border-b pb-1 bg-[#FBFBF9] px-2 py-1 rounded-sm">{foodTypeName}</h5>
                      {canOrder && items.length > 1 ? (
                        <div className="space-y-1">
                          {items.map(item => {
                            const isSelected = selectedRadio === item.id;
                            return (
                              <div
                                key={item.id}
                                className={`flex items-start gap-2 text-sm rounded px-1 py-0.5 cursor-pointer ${isSelected ? SELECTED_BG : ""}`}
                                onClick={() => handleRadioChange(groupKey, foodTypeName, item.id)}
                              >
                                <input
                                  type="radio"
                                  name={`radio-${groupKey}-${foodTypeName}`}
                                  checked={isSelected}
                                  readOnly
                                  className="mt-1 h-4 w-4 accent-primary cursor-pointer"
                                />
                                <span className="flex-1 font-normal">
                                  <span className="text-base">{stripPriceFromName(item.name, group.menuTypeName)}</span>
                                  {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {items.map(item => (
                            <li key={item.id} className={`text-sm flex items-start gap-2 rounded px-1 py-0.5 ${canOrder && items.length === 1 ? SELECTED_BG : ""}`}>
                              {canOrder ? <span className="text-green-600 mt-0.5">✓</span> : <span className="text-muted-foreground">•</span>}
                              <div className="flex-1 break-words">
                                <span className="text-base">{stripPriceFromName(item.name, group.menuTypeName)}</span>
                                {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}

                {/* Variant columns (Menü A, Menü B) */}
                {variantFoodTypes.map(([foodTypeName, { items }]) => {
                  const isThisVariantSelected = selectedVariant === foodTypeName;
                  return (
                    <div
                      key={foodTypeName}
                      className={`space-y-2 break-inside-avoid rounded-md transition-colors ${
                        canOrder && isThisVariantSelected ? `ring-2 ring-primary/30 ${SELECTED_BG} p-2` : canOrder ? "p-2 opacity-70" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {canOrder && (
                          <input
                            type="radio"
                            name={`variant-${groupKey}`}
                            value={foodTypeName}
                            id={`variant-${groupKey}-${foodTypeName}`}
                            checked={isThisVariantSelected}
                           readOnly
                           onClick={() => handleVariantChange(groupKey, foodTypeName)}
                            className="mt-0.5 accent-primary"
                          />
                        )}
                        <h5
                          className="font-medium text-price border-b pb-1 bg-[#FBFBF9] px-2 py-1 rounded-sm flex-1 cursor-pointer"
                          onClick={() => canOrder && handleVariantChange(groupKey, foodTypeName)}
                        >
                          {foodTypeName}
                        </h5>
                      </div>
                      <ul className="space-y-1">
                        {items.map(item => (
                          <li key={item.id} className="text-sm flex items-start gap-2">
                            <span className="text-muted-foreground">•</span>
                            <div className="flex-1 break-words">
                              <span className="text-base">{stripPriceFromName(item.name, group.menuTypeName)}</span>
                              {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            ) : logic === "complete_package" ? (
              /* ── COMPLETE_PACKAGE ── */
              <div className={gridClass}>
                {sortedFoodTypes.map(([foodTypeName, { items }]) => (
                  <div key={foodTypeName} className="space-y-2 break-inside-avoid">
                    <h5 className="font-medium text-price border-b pb-1 bg-[#FBFBF9] px-2 py-1 rounded-sm">{foodTypeName}</h5>
                    <ul className="space-y-1">
                      {items.map(item => (
                        <li key={item.id} className={`text-sm flex items-start gap-2 rounded px-1 py-0.5 ${canOrder && deliverySel ? SELECTED_BG : ""}`}>
                          {canOrder && deliverySel ? (
                            <span className="text-green-600 mt-0.5">✓</span>
                          ) : (
                            <span className="text-muted-foreground">•</span>
                          )}
                          <div className="flex-1 break-words">
                            <span className="text-base">{stripPriceFromName(item.name, group.menuTypeName)}</span>
                            {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : logic === "category_radio" ? (
              /* ── CATEGORY_RADIO ── */
              <div className={gridClass}>
                {sortedFoodTypes.map(([foodTypeName, { items }]) => {
                  const radioKey = `${groupKey}-${foodTypeName}`;
                  const selectedRadio = radioSelections.get(radioKey) || (items.length === 1 ? items[0].id : "");
                  return (
                    <div key={foodTypeName} className="space-y-2 break-inside-avoid">
                      <h5 className="font-medium text-price border-b pb-1 bg-[#FBFBF9] px-2 py-1 rounded-sm">{foodTypeName}</h5>
                      {canOrder && items.length > 1 ? (
                        <div className="space-y-1">
                          {items.map(item => {
                            const isSelected = selectedRadio === item.id;
                            return (
                              <div
                                key={item.id}
                                className={`flex items-start gap-2 text-sm rounded px-1 py-0.5 cursor-pointer ${isSelected ? SELECTED_BG : ""}`}
                                onClick={() => handleRadioChange(groupKey, foodTypeName, item.id)}
                              >
                                <input
                                  type="radio"
                                  name={`radio-${groupKey}-${foodTypeName}`}
                                  checked={isSelected}
                                  readOnly
                                  className="mt-1 h-4 w-4 accent-primary cursor-pointer"
                                />
                                <span className="flex-1 font-normal">
                                  <span className="text-base">{stripPriceFromName(item.name, group.menuTypeName)}</span>
                                  {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {items.map(item => (
                            <li key={item.id} className={`text-sm flex items-start gap-2 rounded px-1 py-0.5 ${canOrder && items.length === 1 ? SELECTED_BG : ""}`}>
                              {canOrder ? <span className="text-green-600 mt-0.5">✓</span> : <span className="text-muted-foreground">•</span>}
                              <div className="flex-1 break-words">
                                <span className="text-base">{stripPriceFromName(item.name, group.menuTypeName)}</span>
                                {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── ITEM_PRICE / MIXED ── */
              <div className={gridClass}>
                {sortedFoodTypes.map(([foodTypeName, { items }]) => (
                  <div key={foodTypeName} className="space-y-2 break-inside-avoid">
                    <h5 className="font-medium text-price border-b pb-1 bg-[#FBFBF9] px-2 py-1 rounded-sm">{foodTypeName}</h5>
                    <ul className="space-y-1">
                      {items.map(item => {
                        const itemHasPrice = item.price != null && item.price > 0;
                        // Find selection for this item across any delivery type
                        let selKey = "";
                        let selData: typeof selectedItems extends Map<string, infer V> ? V : never = undefined as any;
                        for (const [k, v] of selectedItems) {
                          if (k.startsWith(`item-${item.id}-`)) {
                            selKey = k;
                            selData = v;
                            break;
                          }
                        }
                        const isSelected = !!selKey;
                        const isAutoSelected = !itemHasPrice && logic === "mixed" && isCompleteMenuSelected;

                         const showBreakdown = canOrder && isSelected && itemHasPrice;

                        return (
                          <li key={item.id} className="flex flex-col">
                            <div className={`text-sm flex items-start gap-2 rounded px-1 py-0.5 ${isSelected || isAutoSelected ? SELECTED_BG : ""}`}>
                              {canOrder && itemHasPrice ? (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleItemSelection(group.primaryMenuId, item.id, item.name, item.price!)}
                                    className="h-4 w-4"
                                  />
                                </div>
                              ) : canOrder && logic === "mixed" && !itemHasPrice ? (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Checkbox
                                    checked={!!isAutoSelected}
                                    disabled
                                    className="h-4 w-4 opacity-60 cursor-not-allowed"
                                  />
                                </div>
                              ) : (
                                <span className="text-muted-foreground">•</span>
                              )}
                              <div className="flex-1 break-words">
                                <span className="text-base">{stripPriceFromName(item.name, group.menuTypeName)}</span>
                                {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                                {itemHasPrice && <span className="text-price ml-1 font-medium">{item.price} Ft</span>}
                              </div>
                              {canOrder && isSelected && (
                                <div className="flex items-center gap-1">
                                  <Button size="icon" variant="outline" className="h-6 w-6 rounded-full border-2" onClick={() => updateSelectionQuantity(selKey, -1)}>
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <span className="text-xs w-5 text-center font-bold">{selData?.quantity}</span>
                                  <Button size="icon" variant="outline" className="h-6 w-6 rounded-full border-2" onClick={() => updateSelectionQuantity(selKey, 1)}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            {/* Dynamic price breakdown */}
                            {showBreakdown && selData && (() => {
                              const qty = selData.quantity;
                              const currentDT = selData.deliveryType;
                              const bPrice = boxPrice ?? 0;
                              const dFee = deliveryFee ?? 0;
                              const itemTotal = item.price! * qty;
                              const boxTotal = (currentDT === "takeaway" || currentDT === "delivery") && bPrice > 0 ? bPrice * qty : 0;
                              const deliveryTotal = currentDT === "delivery" && dFee > 0 ? dFee : 0;
                              const lineTotal = itemTotal + boxTotal + deliveryTotal;

                              const availableTypes: Array<{ value: "dine_in" | "delivery" | "takeaway"; label: string }> = [];
                              if (takeawayAvailable) availableTypes.push({ value: "takeaway", label: "Elvitel" });
                              if (deliveryAvailable) availableTypes.push({ value: "delivery", label: "Kiszállítás" });

                              return (
                                <div className="ml-7 mt-1 mb-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs space-y-1">
                                  {availableTypes.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-muted-foreground font-medium">Átvétel:</span>
                                      {availableTypes.map(opt => {
                                        const isThisDT = currentDT === opt.value;
                                        return (
                                          <label key={opt.value} className="flex items-center gap-1 cursor-pointer">
                                            <input
                                              type="radio"
                                              name={`item-dt-${item.id}`}
                                              value={opt.value}
                                              checked={isThisDT}
                                              readOnly
                                              onClick={() => {
                                                if (isThisDT) {
                                                  setSelectedItems(prev => {
                                                    const next = new Map(prev);
                                                    next.delete(selKey);
                                                    return next;
                                                  });
                                                } else {
                                                  updateItemDeliveryType(item.id, currentDT, opt.value);
                                                }
                                              }}
                                              className="h-3 w-3 accent-primary cursor-pointer"
                                            />
                                            <span className={isThisDT ? "font-semibold" : ""}>{opt.label}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                  <div className="text-muted-foreground">
                                    {item.price!.toLocaleString("hu-HU")} Ft × {qty}
                                    {boxTotal > 0 && ` + ${bPrice.toLocaleString("hu-HU")} Ft (doboz) × ${qty}`}
                                    {deliveryTotal > 0 && ` + ${dFee.toLocaleString("hu-HU")} Ft (kiszállítás)`}
                                    {" = "}
                                    <span className="font-bold text-foreground">{lineTotal.toLocaleString("hu-HU")} Ft</span>
                                  </div>
                                </div>
                              );
                            })()}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* ── Price bar ── */}
            {priceData.length > 0 && (
              <div className="pt-3 border-t mt-4 bg-[#FBFBF9] px-3 py-2 rounded-sm">
                <div className="text-sm font-medium flex flex-wrap items-center gap-2">
                  {priceData.map((item, index) => {
                    const isOrderable = item.type !== "dine_in";
                    const isSelected = deliverySel?.type === item.type;
                    return (
                      <span key={index} className="flex items-center gap-1">
                        {index > 0 && <span className="text-muted-foreground">|</span>}
                        {canOrder && isOrderable && (
                          <input
                            type="radio"
                            name={`menu-delivery-${groupKey}`}
                            value={item.type}
                            id={`menu-delivery-${groupKey}-${item.type}`}
                            checked={isSelected}
                            disabled={!canSelectDelivery}
                            readOnly
                            onClick={() => !canSelectDelivery ? null : toggleMenuDelivery(groupKey, item.type, item.price)}
                            className={`h-4 w-4 accent-primary ${!canSelectDelivery ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                          />
                        )}
                        <span className={!canSelectDelivery && canOrder && isOrderable ? "opacity-50" : ""}>{item.label}: <span className="text-price">{item.price} Ft</span></span>
                        {canOrder && isOrderable && isSelected && (
                          <span className="flex items-center gap-1 ml-1">
                            <Button size="icon" variant="outline" className="h-6 w-6 rounded-full border-2" onClick={() => updateMenuQuantity(groupKey, -1)}>
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-xs w-5 text-center font-bold">{deliverySel?.quantity}</span>
                            <Button size="icon" variant="outline" className="h-6 w-6 rounded-full border-2" onClick={() => updateMenuQuantity(groupKey, 1)}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Soup-only / Main-course-only fallback info */}
            {canOrder && hasMenuPrices && (soupOnlyPrice || mainCourseOnlyPrice) && (
              <div className="text-xs text-muted-foreground italic px-1">
                {soupOnlyPrice && <span>Csak leves: {soupOnlyPrice} Ft</span>}
                {soupOnlyPrice && mainCourseOnlyPrice && <span> | </span>}
                {mainCourseOnlyPrice && <span>Csak főétel: {mainCourseOnlyPrice} Ft</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
